/**
 * Signup invite links — `app.founding.dev/auth?invite=<code>` (any app path
 * works). Not team invites (/invite/:token — see lib/pendingInvite.ts). The code is staff-created in the ops admin panel (Invite links)
 * and, once redeemed, labels the person (e.g. "Beta tester"), grants access
 * and tags them in Customer.io — see server/services/signupInviteService.js.
 *
 * The code has to survive the Google/GitHub OAuth round trip and possibly a
 * closed tab before sign-up, so it waits in localStorage (30 days) until
 * AuthContext redeems it for whoever is signed in. Cleared on a definite
 * answer (redeemed / invalid); kept for a retry on a network error or 429.
 */

const STORAGE_KEY = 'fd_signup_invite';
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
const CODE_RE = /^[a-z0-9][a-z0-9-]{2,39}$/;

/** Stash `?invite=` off the current URL. Call once per app boot. */
export function captureSignupInvite(): void {
  try {
    const raw = new URLSearchParams(window.location.search).get('invite');
    const code = raw ? raw.trim().toLowerCase() : '';
    if (!CODE_RE.test(code)) return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ code, at: Date.now() }));
  } catch {
    /* invite capture must never break app boot */
  }
}

export function readSignupInvite(): string | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const { code, at } = JSON.parse(raw);
    if (typeof code !== 'string' || typeof at !== 'number' || Date.now() - at > MAX_AGE_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return code;
  } catch {
    return null;
  }
}

export function clearSignupInvite(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
